import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { usePrefersReducedMotion } from '@lumapps/a11y/hooks/usePreferReducedMotion';
import { useClassnames, margin } from '@lumapps/classnames';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { useFocusWithin } from '@lumapps/lumx-hooks/hooks/useFocusWithin';
import { mdiChevronDown, mdiChevronUp } from '@lumapps/lumx/icons';
import { FlexBox, Link as LumxLink, Typography, Size, FlexBoxProps } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import './index.scss';

export interface SeeMoreFadeProps extends FlexBoxProps {
    /* Class name for main div */
    className?: string;
    /** if you don't want to display "see more" button */
    hideSeeMore?: boolean;
    /** height before hiding with fade. Default is 80px */
    maxHeight?: number;
    /** Whether the component should toggle open when focus is received within. Default to true */
    openOnFocus?: boolean;
    /**
     * A primitive value that will re-trigger a size calculation on change.
     * Recalculation will only be trigger if the "show more" is not already displayed
     * */
    recalculationSeed?: string | number | boolean;
    /** Allow to use a custom toggle button instead of the default one */
    renderToggleButton?: (toggleDisplay: () => void, isMoreOpen?: boolean) => ReactNode;
    /** Whether hovering the element should display the content */
    previewOnHover?: boolean;
    /** Called whenever the content is explicitly opened/closed (click, toggle button, focus) */
    onOpenChange?: (isOpen: boolean) => void;
    /** Called whenever the hover preview is activated/deactivated */
    onPreviewChange?: (isPreviewActive: boolean) => void;
}

const DEFAULT_MAX_HEIGHT = 80;
const PREVIEW_DEBOUNCE = 250;

const CLASSNAME = 'see-more-fade';

/**
 * Component for display content with button See more / less if bigger than specified height
 * @param SeeMoreFadeProps
 * @returns SeeMoreFade
 * @family Layouts
 */
export const SeeMoreFade: React.FC<SeeMoreFadeProps> = ({
    children,
    className,
    recalculationSeed,
    hideSeeMore = false,
    maxHeight = DEFAULT_MAX_HEIGHT,
    openOnFocus = !hideSeeMore,
    previewOnHover = false,
    renderToggleButton,
    onOpenChange,
    onPreviewChange,
    ...wrapperProps
}) => {
    const { translateKey } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);
    const contentRef = useRef<HTMLDivElement>(null);
    const [hasMore, , setHasMoreFalse, setHasMoreTrue] = useBooleanState(false);
    const [isMoreOpen, toggleMoreOpen, , openMore] = useBooleanState(false, onOpenChange);

    const [isPreviewActive, togglePreview, disablePreview] = useBooleanState(false, onPreviewChange);

    const [shouldCalculate, setShouldCalculate] = useState(true);

    const isPreferredReducedMotion = usePrefersReducedMotion();

    const shouldPreviewOnHover = previewOnHover && !isPreferredReducedMotion;

    /**
     * If the height of the div in which the content is located is higher
     * than specified maxHeight, then display SeeMore button
     * The element needs to be fully loaded
     */
    useEffect(() => {
        if (contentRef.current && contentRef.current.clientHeight > maxHeight && shouldCalculate) {
            setHasMoreTrue();
            setShouldCalculate(false);
        }
    }, [maxHeight, setHasMoreFalse, setHasMoreTrue, recalculationSeed, shouldCalculate]);

    // Open element on focus in order to improve accessibility
    useFocusWithin({
        elementRef: contentRef,
        onFocusIn: openMore,
        shouldFocus: openOnFocus,
    });

    /** Callback to expand the element to preview the data */
    const handleShowPreview = useCallback(() => {
        if (!isPreviewActive && !isMoreOpen) {
            togglePreview();
        }
    }, [isPreviewActive, isMoreOpen, togglePreview]);

    /**
     * To avoid having the content being displayed on each mouse enter, we can
     * debounce the display on mouseenter and cancel it on mouse leave to only show the preview
     * if the user remains on the element for a defined amount of time.
     */
    const debounceShowPreview = useMemo(() => debounce(handleShowPreview, PREVIEW_DEBOUNCE), [handleShowPreview]);

    // Cancel any pending debounced preview when the debounce gets replaced or on unmount
    useEffect(() => () => debounceShowPreview.cancel(), [debounceShowPreview]);

    /** Callback to hide the preview  */
    const handleDisablePreview = () => {
        if (!isMoreOpen) {
            debounceShowPreview.cancel();
            disablePreview();
        }
    };

    /**
     * The height to currently display
     * When showing a preview, we need to set the max height to the content's height to
     * trigger the css animation.
     *
     * However we need to reset it to undefined once opened as the content might change height and should
     * therefore not be bound to a max height.
     */
    const currentHeight = useMemo(() => {
        if (!hasMore || isMoreOpen) {
            return undefined;
        }
        // If we're currently displaying a preview, set the height to the current height of the content
        return isPreviewActive ? contentRef.current?.scrollHeight : maxHeight;
    }, [hasMore, isMoreOpen, isPreviewActive, maxHeight]);

    return (
        <FlexBox
            /** Disable the preview when the mouse leaves the element */
            onMouseLeave={shouldPreviewOnHover ? handleDisablePreview : undefined}
            className={block([className])}
            {...wrapperProps}
        >
            <FlexBox
                ref={contentRef}
                /**
                 * Preview the content on hover when prop it set to true.
                 * The onMouseEnter must be set here instead of the parent element to
                 * allow users to click on the "show more" button without triggering the preview
                 */
                onMouseEnter={shouldPreviewOnHover ? debounceShowPreview : undefined}
                // Expand the element when openOnFocus is set
                onClick={openOnFocus && !isMoreOpen ? openMore : undefined}
                className={element('box', {
                    'overflow-hidden': hasMore && !isMoreOpen,
                    preview: isPreviewActive && !isMoreOpen,
                })}
                style={{
                    maxHeight: currentHeight,
                }}
            >
                {children}
            </FlexBox>

            {!hideSeeMore &&
                hasMore &&
                (renderToggleButton ? (
                    renderToggleButton(toggleMoreOpen, isMoreOpen)
                ) : (
                    <LumxLink
                        className={margin('top', Size.regular)}
                        leftIcon={isMoreOpen ? mdiChevronUp : mdiChevronDown}
                        typography={Typography.body1}
                        onClick={toggleMoreOpen}
                    >
                        {translateKey(isMoreOpen ? GLOBAL.SHOW_LESS : GLOBAL.SHOW_MORE)}
                    </LumxLink>
                ))}
        </FlexBox>
    );
};
